import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert, Divider, InputAdornment,
} from '@mui/material';
import { PlayArrow, Stop, PointOfSale, Print } from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import api from '../../api/axios';

const PKR_DENOMS = [5000, 1000, 500, 100, 50, 20, 10, 5, 2, 1];

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-PK') : '—';

function OpenShiftDialog({ open, onClose, onSuccess }) {
  const [float, setFloat] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async () => {
    if (!float && float !== 0) { setError('Enter opening float amount'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/shifts/open', { float_amount: +float, notes });
      onSuccess();
    } catch (e) { setError(e.response?.data?.message || 'Failed to open shift'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Open Cashier Shift</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Opening Float (₨)" type="number" value={float} onChange={e => setFloat(e.target.value)} fullWidth autoFocus inputProps={{ min: 0 }} helperText="Cash in drawer at shift start" />
        <TextField label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} fullWidth />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" onClick={handle} disabled={loading} startIcon={<PlayArrow />}>
          {loading ? <CircularProgress size={20} /> : 'Open Shift'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Z-Report printable component
function ZReport({ shift, result, denoms, cashTotal }) {
  return (
    <Box sx={{ p: 3, fontFamily: 'monospace', fontSize: 12, color: '#000', maxWidth: 320 }}>
      <style>{`@media print { @page { size: 80mm; margin: 0; } }`}</style>
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        <Typography sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: 15 }}>Z — SHIFT REPORT</Typography>
        <Typography sx={{ fontFamily: 'inherit' }}>{new Date().toLocaleString('en-PK')}</Typography>
        <Typography sx={{ fontFamily: 'inherit' }}>{'='.repeat(36)}</Typography>
      </Box>
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontFamily: 'inherit' }}>Shift opened: {new Date(shift?.opened_at).toLocaleString('en-PK')}</Typography>
        <Typography sx={{ fontFamily: 'inherit' }}>Shift closed: {new Date().toLocaleString('en-PK')}</Typography>
        <Typography sx={{ fontFamily: 'inherit' }}>Cashier: {shift?.cashier_name || '—'}</Typography>
      </Box>
      <Typography sx={{ fontFamily: 'inherit' }}>{'-'.repeat(36)}</Typography>
      {[
        ['Total Sales',    `Rs.${fmt(result.total_sales)}`],
        ['Invoices',       String(result.sale_count || 0)],
        ['Cash Sales',     `Rs.${fmt(result.cash_sales)}`],
        ['Card/Other',     `Rs.${fmt((result.total_collected||0)-(result.cash_sales||0))}`],
        ['Total Collected',`Rs.${fmt(result.total_collected)}`],
      ].map(([l,v]) => (
        <Typography key={l} sx={{ fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between' }}>
          <span>{l}</span><span>{v}</span>
        </Typography>
      ))}
      <Typography sx={{ fontFamily: 'inherit' }}>{'-'.repeat(36)}</Typography>
      <Typography sx={{ fontFamily: 'inherit', fontWeight: 700 }}>CASH COUNT (Denomination)</Typography>
      {PKR_DENOMS.filter(d => (denoms[d] || 0) > 0).map(d => (
        <Typography key={d} sx={{ fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between' }}>
          <span>  Rs.{fmt(d)} x {denoms[d]}</span>
          <span>Rs.{fmt(d * denoms[d])}</span>
        </Typography>
      ))}
      <Typography sx={{ fontFamily: 'inherit' }}>{'-'.repeat(36)}</Typography>
      {[
        ['Opening Float',  `Rs.${fmt(shift?.float_amount)}`],
        ['Expected Cash',  `Rs.${fmt(result.expected_cash)}`],
        ['Closing Count',  `Rs.${fmt(cashTotal)}`],
      ].map(([l,v]) => (
        <Typography key={l} sx={{ fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between' }}>
          <span>{l}</span><span>{v}</span>
        </Typography>
      ))}
      <Typography sx={{ fontFamily: 'inherit', fontWeight: 700, color: result.variance >= 0 ? 'green' : 'red', display: 'flex', justifyContent: 'space-between' }}>
        <span>VARIANCE</span>
        <span>{result.variance >= 0 ? '+' : ''}Rs.{fmt(result.variance)}</span>
      </Typography>
      <Typography sx={{ fontFamily: 'inherit' }}>{'='.repeat(36)}</Typography>
      <Typography sx={{ fontFamily: 'inherit', textAlign: 'center' }}>
        {Math.abs(result.variance) <= 10 ? '✓ Shift Balanced' : '⚠ Please investigate variance'}
      </Typography>
    </Box>
  );
}

function CloseShiftDialog({ shift, open, onClose, onSuccess }) {
  const [denoms, setDenoms] = useState(() => Object.fromEntries(PKR_DENOMS.map(d => [d, ''])));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const printRef = useRef();
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const cashTotal = PKR_DENOMS.reduce((s, d) => s + (+denoms[d] || 0) * d, 0);

  const setDenom = (d, v) => setDenoms(prev => ({ ...prev, [d]: v }));

  const handle = async () => {
    if (cashTotal === 0 && !window.confirm('Cash count is ₨0. Continue?')) return;
    setLoading(true); setError('');
    try {
      const { data } = await api.post(`/shifts/${shift.id}/close`, {
        closing_amount: cashTotal,
        closing_notes: notes,
        denomination_count: denoms,
      });
      setResult(data.data);
    } catch (e) { setError(e.response?.data?.message || 'Failed to close shift'); }
    finally { setLoading(false); }
  };

  const reset = () => { setResult(null); setDenoms(Object.fromEntries(PKR_DENOMS.map(d => [d, '']))); setNotes(''); };

  return (
    <Dialog open={open} onClose={result ? () => { reset(); onSuccess(); } : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Close Shift — Cash Count & Z Report</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!result ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Shift opened: {fmtTime(shift?.opened_at)} — Float: ₨ {fmt(shift?.float_amount)}
            </Alert>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Count Cash in Drawer</Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Denomination</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PKR_DENOMS.map(d => (
                  <TableRow key={d} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>₨ {fmt(d)}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        size="small" type="number" value={denoms[d]}
                        onChange={e => setDenom(d, e.target.value)}
                        inputProps={{ min: 0, style: { width: 80 } }}
                        placeholder="0"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {(+denoms[d] || 0) > 0
                        ? <Typography fontWeight={600} color="primary">₨ {fmt(d * +denoms[d])}</Typography>
                        : <Typography color="text.disabled">—</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Cash Count</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 16 }}>₨ {fmt(cashTotal)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <TextField label="Closing Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} fullWidth size="small" />
          </Box>
        ) : (
          <Box>
            {/* Hidden Z-Report for print */}
            <Box sx={{ display: 'none' }}>
              <Box ref={printRef}>
                <ZReport shift={shift} result={result} denoms={denoms} cashTotal={cashTotal} />
              </Box>
            </Box>

            <Alert severity={Math.abs(result.variance) <= 10 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {Math.abs(result.variance) <= 10 ? '✓ Shift balanced!' : `⚠ Variance detected: ₨ ${fmt(Math.abs(result.variance))}`}
            </Alert>

            <Divider sx={{ mb: 2 }}><Typography variant="caption">Sales Summary</Typography></Divider>
            {[
              ['Total Sales',     `₨ ${fmt(result.total_sales)}`],
              ['Total Collected', `₨ ${fmt(result.total_collected)}`],
              ['Cash Sales',      `₨ ${fmt(result.cash_sales)}`],
            ].map(([label, val]) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography color="text.secondary">{label}</Typography>
                <Typography fontWeight={600}>{val}</Typography>
              </Box>
            ))}

            <Divider sx={{ my: 2 }}><Typography variant="caption">Cash Reconciliation</Typography></Divider>
            {[
              ['Opening Float',  `₨ ${fmt(shift?.float_amount)}`],
              ['Cash Sales',     `₨ ${fmt(result.cash_sales)}`],
              ['Expected Cash',  `₨ ${fmt(result.expected_cash)}`],
              ['Your Count',     `₨ ${fmt(cashTotal)}`],
            ].map(([label, val]) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography color="text.secondary">{label}</Typography>
                <Typography fontWeight={600}>{val}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, mt: 1, bgcolor: Math.abs(result.variance) <= 10 ? 'success.50' : 'warning.50', px: 1, borderRadius: 1 }}>
              <Typography fontWeight={700} color={result.variance >= 0 ? 'success.main' : 'error.main'}>VARIANCE</Typography>
              <Typography fontWeight={700} color={result.variance >= 0 ? 'success.main' : 'error.main'} fontSize={18}>
                {result.variance >= 0 ? '+' : ''}₨ {fmt(result.variance)}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!result ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handle} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <Stop />}>
              Close Shift
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>Print Z-Report</Button>
            <Button variant="contained" onClick={() => { reset(); onSuccess(); }}>Done</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function ShiftsPage() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [currentShift, setCurrentShift] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, curRes] = await Promise.all([
        api.get('/shifts'),
        api.get('/shifts/current'),
      ]);
      setShifts(listRes.data.data || []);
      setCurrentShift(curRes.data.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>Shift Management</Typography>

      {/* Current shift banner */}
      <Card sx={{ mb: 3, border: '2px solid', borderColor: currentShift ? 'success.main' : 'divider' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} color={currentShift ? 'success.main' : 'text.secondary'}>
                {currentShift ? 'Shift Open' : 'No Active Shift'}
              </Typography>
              {currentShift && (
                <Typography variant="body2" color="text.secondary">
                  Opened at {fmtTime(currentShift.opened_at)} — Float: ₨ {fmt(currentShift.float_amount)}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {currentShift ? (
                <>
                  <Button variant="contained" startIcon={<PointOfSale />} onClick={() => navigate('/pos')}>Go to POS</Button>
                  <Button variant="outlined" color="error" startIcon={<Stop />} onClick={() => setCloseDialog(true)}>Close Shift</Button>
                </>
              ) : (
                <Button variant="contained" color="success" startIcon={<PlayArrow />} onClick={() => setOpenDialog(true)}>Open Shift</Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Shift history */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>Shift History</Typography>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cashier</TableCell>
                  <TableCell>Branch</TableCell>
                  <TableCell>Opened</TableCell>
                  <TableCell>Closed</TableCell>
                  <TableCell align="right">Float</TableCell>
                  <TableCell align="right">Sales</TableCell>
                  <TableCell align="right">Cash</TableCell>
                  <TableCell align="right">Variance</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shifts.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.cashier_name}</TableCell>
                    <TableCell>{s.branch_name}</TableCell>
                    <TableCell><Typography variant="caption">{fmtTime(s.opened_at)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmtTime(s.closed_at)}</Typography></TableCell>
                    <TableCell align="right">₨ {fmt(s.float_amount)}</TableCell>
                    <TableCell align="right">₨ {fmt(s.total_sales)}</TableCell>
                    <TableCell align="right">₨ {fmt(s.cash_sales)}</TableCell>
                    <TableCell align="right">
                      {s.status === 'closed' && (
                        <Typography fontWeight={600} color={s.variance >= 0 ? 'success.main' : 'error.main'}>
                          {s.variance >= 0 ? '+' : ''}₨ {fmt(s.variance)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={s.status} size="small" color={s.status === 'open' ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
                {shifts.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No shifts yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <OpenShiftDialog open={openDialog} onClose={() => setOpenDialog(false)} onSuccess={() => { setOpenDialog(false); load(); }} />
      {currentShift && (
        <CloseShiftDialog shift={currentShift} open={closeDialog} onClose={() => setCloseDialog(false)} onSuccess={() => { setCloseDialog(false); load(); }} />
      )}
    </Box>
  );
}
